import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from '../store/hooks';
import {
  fetchCrateById,
  setSelectedCrate,
  addTrackToCrate,
  saveCrateChanges,
} from '../store/slices/crate.slice';
import { fetchTrackById } from '../store/slices/track.slice';
import { CrateService } from '../services/Crate.service';
import CrateUpdateService from '../services/CrateUpdateService';
import { useToast } from '../components/ToastManager/useToast';
import { iTrack } from '../types/ITrack';

interface UseAddToCrateOptions {
  track: iTrack;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const useAddToCrate = ({
  track,
  onSuccess,
  onError,
}: UseAddToCrateOptions) => {
  const [crates, setCrates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [showPopover, setShowPopover] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const { successToast, errorToast } = useToast();

  // Fetch crates using CrateService
  const getMyCrates = useCallback(() => {
    setIsLoading(true);
    CrateService.fetchSelectedCrates(
      [CrateService.MY_CRATES],
      (fetchedCrates) => {
        setCrates(fetchedCrates);
        setIsLoading(false);
      },
      undefined,
      history,
    );
  }, [history]);

  useEffect(() => {
    if (showPopover) {
      getMyCrates();
    }
  }, [showPopover, getMyCrates]);

  const filteredCrates = crates.filter((crate) =>
    crate.Title?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleAddTrackToCrate = async (crate: any) => {
    try {
      // First, fetch the complete crate data by ID
      const crateResult = await dispatch(fetchCrateById(crate._id));
      if (fetchCrateById.fulfilled.match(crateResult) && crateResult.payload) {
        const fullCrateData = crateResult.payload;

        const trackInCrate = fullCrateData.Tracks?.find(
          (crateTrack: any) => crateTrack.SpotifyID === track.ID,
        );

        // Avoid adding a track that is already in the crate
        if (trackInCrate) {
          const errorMessage = `${trackInCrate.Title} already exists in ${fullCrateData.Title}`;
          errorToast({
            message: errorMessage,
          });
          setShowPopover(false);
          onError?.(errorMessage);
          return;
        }

        // Set the selected crate with the full data we fetched
        dispatch(setSelectedCrate(fullCrateData));

        // Fetch full track data
        const trackResult = await dispatch(fetchTrackById(track.ID));

        if (
          fetchTrackById.fulfilled.match(trackResult) &&
          trackResult.payload
        ) {
          await dispatch(addTrackToCrate(trackResult.payload));
          await dispatch(saveCrateChanges());

          CrateUpdateService.notifyCrateUpdated(crate._id);

          setShowPopover(false);
          const successMessage = `Added new track to ${crate?.Title}`;
          successToast({
            message: successMessage,
          });
          onSuccess?.();
        }
      }
    } catch (error) {
      console.error('Error adding track:', error);
      const errorMessage = 'Unable to add track';
      errorToast({ message: errorMessage });
      setShowPopover(false);
      onError?.(errorMessage);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPopover(true);
  };

  return {
    crates: filteredCrates,
    isLoading,
    searchText,
    setSearchText,
    showPopover,
    setShowPopover,
    handleAddTrackToCrate,
    handleContextMenu,
  };
};
